import { EMPTY_OBJECT } from "@client/lib/misc";
import { type GenericMap } from "@client/types";
import { useI18n } from "@common/hooks/i18n";
import React from "react";
import { Context } from "./Content/context";

export interface IWithValidate {
  validate: () => Promise<string | boolean | null>;
}

export type IRef = IWithValidate & {
  getFinalValues: () => Promise<GenericMap<any> | null>;
};

export const withOnSubmit = <T extends IRef>(mapFn?: (data: any) => any) => {
  const i18n = useI18n();

  const ref = React.useRef<T>(null);
  const context = React.useContext(Context);

  React.useEffect(() => {
    const listener = {
      getValue: async (): Promise<GenericMap<any>> => {
        // can be null in conditional rendering e.g. networkInformation component
        if (!ref.current) return EMPTY_OBJECT;

        const res = await ref.current!.getFinalValues();
        return mapFn ? mapFn(res) : {};
      },
      validate: async (): Promise<string | null> => {
        if (!ref.current) return Promise.resolve(null);

        const res = await ref.current!.validate();
        return res === true ? null : res === false ? i18n.invalid : res;
      }
    };

    context.subscribeOnSubmit(listener);

    return () => {
      context.unsubscribeOnSubmit(listener);
    };
  }, [context.subscribeOnSubmit, context.unsubscribeOnSubmit]);

  return ref;
};
